package com.evoapps.evoshift.navigation

import kotlinx.coroutines.flow.MutableStateFlow
import kotlinx.coroutines.flow.StateFlow
import kotlinx.coroutines.flow.asStateFlow

/**
 * SH-105: the detail panes open on wide windows (an employee beside the team, a day beside the
 * daily records), innermost last. They are not routes: a pane is a selection inside its screen,
 * so deep links and the back stack stay as they were. Back closes the newest pane before it
 * leaves the screen: the system back (ui/adaptive DetailPaneBack), the desktop's View > Back
 * (App.kt) and the browser's back button (BrowserBack.js counts the open panes as history
 * entries above the screen's).
 */
object DetailPanes {
    private class Entry(val token: Long, val close: () -> Unit)

    private val entries = mutableListOf<Entry>()
    private var nextToken = 0L
    private val _open = MutableStateFlow(0)

    /** How many panes are open now. */
    val open: StateFlow<Int> = _open.asStateFlow()

    /** Registers an open pane that [close] closes; returns its token for [closed]. */
    fun opened(close: () -> Unit): Long {
        val token = nextToken++
        entries += Entry(token, close)
        _open.value = entries.size
        return token
    }

    /** The pane of [token] is gone (closed, or its screen left). */
    fun closed(token: Long) {
        if (entries.removeAll { it.token == token }) _open.value = entries.size
    }

    /** Closes the newest open pane; false when none is open (back then leaves the screen). */
    fun closeNewest(): Boolean {
        val newest = entries.removeLastOrNull() ?: return false
        _open.value = entries.size
        newest.close()
        return true
    }
}
